/**
 * Hook for polling search index health status
 *
 * Uses the /search/health endpoint to get real-time status of all search indexes.
 */

import { useQuery } from '@tanstack/react-query'

const API_BASE = import.meta.env.VITE_API_URL || 'https://grobotic.app:2096'

export interface IndexStats {
  ready: boolean
  documentCount: number
  lastSyncTime: string | null
  isInitialized: boolean
  memoryEstimateMB: number
}

export interface SearchHealthData {
  configured: boolean
  healthy: boolean
  message: string
  organizations: IndexStats
  deals: IndexStats
  products: IndexStats
  mtb: IndexStats
}

const fetchSearchHealth = async (): Promise<SearchHealthData> => {
  const response = await fetch(`${API_BASE}/search/health`)
  if (!response.ok) {
    throw new Error(`Health check failed: ${response.statusText}`)
  }
  return response.json()
}

export const SEARCH_HEALTH_KEY = ['search', 'health'] as const

/**
 * Hook for polling search health status
 *
 * @param pollMs - Polling interval in milliseconds (default: 60000ms / 1 minute)
 * @param enabled - Whether to enable polling (default: true)
 */
export const useSearchHealth = (pollMs: number = 60_000, enabled: boolean = true) => {
  return useQuery({
    queryKey: SEARCH_HEALTH_KEY,
    queryFn: fetchSearchHealth,
    enabled,
    staleTime: pollMs / 2, // Consider stale after half the poll interval
    gcTime: 5 * 60_000, // Keep in cache for 5 minutes
    refetchInterval: pollMs,
    refetchIntervalInBackground: false, // Don't poll when tab is not active
    retry: 1,
  })
}

/**
 * Force refresh all search indexes
 */
export const triggerSearchRefresh = async (): Promise<{ success: boolean; message?: string; error?: string }> => {
  try {
    const response = await fetch(`${API_BASE}/search/cache/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    })

    const data = await response.json()

    if (!response.ok) {
      return { success: false, error: data.message || 'Refresh failed' }
    }

    return { success: true, message: data.message }
  } catch (err: any) {
    return { success: false, error: err.message }
  }
}

export default useSearchHealth
